import { Alert, Button, Paper } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DoorVisitTable } from '../DoorVisitTable';
import { DoorVisitReportControls, type DoorVisitReportRequestIdGenerator } from '../DoorVisitReportControls';
import { createDoorVisitReportApi, type DoorVisitReportApi } from '../doorVisitReportApi';
import { BrokerageCanvasserDirectoryFailureNotice } from '../BrokerageCanvasserDirectoryFailureNotice';
import { useBrokerageCanvasserDirectory } from '../BrokerageCanvasserDirectoryContext';
import { EmptyState } from '../EmptyState';
import { ErrorState } from '../ErrorState';
import { getDefaultCustomDateRange } from '../filters/calendarDateRange';
import { DoorVisitFilterBar } from '../filters/DoorVisitFilterBar';
import {
  clearFilters,
  filtersToSearchParams,
  hasActiveFilters,
  isDoorVisitCustomDateRangeValid,
  parseFilters,
  type DoorVisitDateRangePreset,
  type DoorVisitFilterState,
} from '../filters/doorVisitFilters';
import { LoadingState } from '../LoadingState';
import { PageHeader } from '../PageHeader';
import { useDoorVisitFilterOptions } from '../useDoorVisitFilterOptions';
import { useDoorVisitList, type UseDoorVisitListOptions } from '../useDoorVisitList';

export type DoorVisitsPageProps = Pick<UseDoorVisitListOptions, 'fetchImpl'> & {
  reportApi?: DoorVisitReportApi;
  reportRequestIdGenerator?: DoorVisitReportRequestIdGenerator;
};

export const DoorVisitsPage = ({ fetchImpl, reportApi: reportApiOverride, reportRequestIdGenerator }: DoorVisitsPageProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = parseFilters(searchParams);
  const [customDraft, setCustomDraft] = useState<{ fromDate: string; throughDate: string } | null>(null);
  const appliedDateKey = JSON.stringify([filters.dateRange, filters.fromDate, filters.throughDate]);
  const previousAppliedDateKey = useRef(appliedDateKey);
  useEffect(() => {
    if (previousAppliedDateKey.current === appliedDateKey) return;
    previousAppliedDateKey.current = appliedDateKey;
    if (
      customDraft !== null &&
      (filters.dateRange !== 'custom' ||
        customDraft.fromDate !== (filters.fromDate ?? '') ||
        customDraft.throughDate !== (filters.throughDate ?? ''))
    ) {
      setCustomDraft(null);
    }
  }, [appliedDateKey, customDraft, filters.dateRange, filters.fromDate, filters.throughDate]);

  const dateRange: DoorVisitDateRangePreset = customDraft === null ? filters.dateRange : 'custom';
  const customFromDate = customDraft?.fromDate ?? filters.fromDate ?? '';
  const customThroughDate = customDraft?.throughDate ?? filters.throughDate ?? '';
  const listFilters = customDraft === null
    ? filters
    : {
        ...filters,
        dateRange: 'custom' as const,
        fromDate: customFromDate || undefined,
        throughDate: customThroughDate || undefined,
      };
  const customRangeInvalid = dateRange === 'custom' &&
    !isDoorVisitCustomDateRangeValid(customFromDate || undefined, customThroughDate || undefined);
  const { resolveLabel: resolveCanvasserLabel } = useBrokerageCanvasserDirectory();
  const reportApi = useMemo(
    () => reportApiOverride ?? createDoorVisitReportApi({ fetchImpl }),
    [fetchImpl, reportApiOverride],
  );
  const { items, total, loading, error, page, rowsPerPage, calendarTimeZone, setPage, setRowsPerPage, refetch } = useDoorVisitList({
    fetchImpl,
    filters: listFilters,
    enabled: !customRangeInvalid,
  });

  const handleFiltersChange = (next: DoorVisitFilterState): void => {
    setSearchParams(filtersToSearchParams(next), { replace: true });
  };

  const handleDateRangeChange = (nextDateRange: DoorVisitDateRangePreset): void => {
    if (nextDateRange === 'custom') {
      const range = filters.dateRange === 'custom'
        ? { fromDate: filters.fromDate ?? '', throughDate: filters.throughDate ?? '' }
        : getDefaultCustomDateRange(new Date(), calendarTimeZone);
      setCustomDraft(null);
      setSearchParams(filtersToSearchParams({ ...filters, dateRange: 'custom', ...range }), { replace: true });
      return;
    }

    setCustomDraft(null);
    setSearchParams(
      filtersToSearchParams({ ...filters, dateRange: nextDateRange, fromDate: undefined, throughDate: undefined }),
      { replace: true },
    );
  };

  const handleCustomRangeChange = (fromDate: string, throughDate: string): void => {
    setCustomDraft({ fromDate, throughDate });
    if (isDoorVisitCustomDateRangeValid(fromDate || undefined, throughDate || undefined)) {
      setSearchParams(
        filtersToSearchParams({ ...filters, dateRange: 'custom', fromDate, throughDate }),
        { replace: true },
      );
    }
  };

  const {
    canvasserIds,
    customSlots,
    error: filterOptionsError,
    refetch: refetchFilterOptions,
  } = useDoorVisitFilterOptions({ fetchImpl });

  const handleRowClick = (doorVisitId: number): void => {
    void navigate(`/door-visits/${doorVisitId}`);
  };

  const filtersActive = hasActiveFilters(listFilters);

  return (
    <>
      <PageHeader title="Door Visits" subtitle="All recorded canvassing conversations." />
      <DoorVisitReportControls
        filters={listFilters}
        api={reportApi}
        requestIdGenerator={reportRequestIdGenerator}
        disabled={customRangeInvalid}
      />
      <Paper variant="outlined">
        <DoorVisitFilterBar
          filters={listFilters}
          onChange={handleFiltersChange}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          customFromDate={customFromDate}
          customThroughDate={customThroughDate}
          onCustomRangeChange={handleCustomRangeChange}
          calendarTimeZone={calendarTimeZone}
          canvasserOptions={canvasserIds}
          customSlots={customSlots}
          resolveCanvasserLabel={resolveCanvasserLabel}
        />
        <BrokerageCanvasserDirectoryFailureNotice />
        {filterOptionsError !== null && (
          <Alert
            severity="warning"
            sx={{ mx: 2, mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={refetchFilterOptions}>
                Retry
              </Button>
            }
          >
            Couldn&apos;t load filter options. Any canvasser or custom slot filter already applied is still in effect.
          </Alert>
        )}
        {!customRangeInvalid && loading && <LoadingState />}
        {!customRangeInvalid && !loading && error !== null && <ErrorState message={error} onRetry={refetch} />}
        {!customRangeInvalid && !loading && error === null && items.length === 0 && (
          <EmptyState
            title={filtersActive ? 'No visits match these filters' : 'No door visits yet'}
            hint={
              filtersActive
                ? 'Try widening the date range or clearing a filter.'
                : 'Door visit records will appear here once field data is connected.'
            }
            action={
              filtersActive ? (
                <Button size="small" onClick={() => handleFiltersChange(clearFilters(filters))}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        )}
        {!customRangeInvalid && !loading && error === null && items.length > 0 && (
          <DoorVisitTable
            items={items}
            total={total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            onRowClick={handleRowClick}
          />
        )}
      </Paper>
    </>
  );
};
